import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { scopeService, } from '../services/scopeService';
import { isPermissionDenied } from '../utils/grpcErrors';
import { useAccessSettlingRetry } from './useAccessSettlingRetry';
import * as Cesium from 'cesium';
function getCameraPosition(viewer) {
    const cam = viewer.camera;
    const cartographic = cam.positionCartographic;
    return {
        latitude: Cesium.Math.toDegrees(cartographic.latitude),
        longitude: Cesium.Math.toDegrees(cartographic.longitude),
        height: cartographic.height,
        heading: Cesium.Math.toDegrees(cam.heading),
        pitch: Cesium.Math.toDegrees(cam.pitch),
        roll: Cesium.Math.toDegrees(cam.roll),
    };
}
function flyToCamera(viewer, cam) {
    viewer.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(cam.longitude, cam.latitude, cam.height),
        orientation: {
            heading: Cesium.Math.toRadians(cam.heading),
            pitch: Cesium.Math.toRadians(cam.pitch),
            roll: Cesium.Math.toRadians(cam.roll),
        },
        duration: 1.5,
    });
}
export const useScopes = ({ projectId, viewer, getLayerConfigs, getArtifactConfigs, applyLayerConfigs, applyArtifactConfigs, }) => {
    const [scopes, setScopes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [denied, setDenied] = useState(false);
    const [activeScopeId, setActiveScopeId] = useState(null);
    const isLoadingRef = useRef(false);
    const preScopeStateRef = useRef(null);
    const viewerRef = useRef(viewer);
    const getLayerConfigsRef = useRef(getLayerConfigs);
    const getArtifactConfigsRef = useRef(getArtifactConfigs);
    const applyLayerConfigsRef = useRef(applyLayerConfigs);
    const applyArtifactConfigsRef = useRef(applyArtifactConfigs);
    const scopesRef = useRef(scopes);
    const activeScopeIdRef = useRef(activeScopeId);
    const projectIdRef = useRef(projectId);
    viewerRef.current = viewer;
    getLayerConfigsRef.current = getLayerConfigs;
    getArtifactConfigsRef.current = getArtifactConfigs;
    applyLayerConfigsRef.current = applyLayerConfigs;
    applyArtifactConfigsRef.current = applyArtifactConfigs;
    scopesRef.current = scopes;
    activeScopeIdRef.current = activeScopeId;
    projectIdRef.current = projectId;
    const scopeNames = useMemo(() => scopes.map(s => s.name), [scopes]);
    const fetchScopes = useCallback(async () => {
        if (!projectId || isLoadingRef.current)
            return;
        try {
            isLoadingRef.current = true;
            setLoading(true);
            setError(null);
            const result = await scopeService.getScopes(projectId);
            setScopes(result);
            setDenied(false);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load scopes';
            console.error('[useScopes] Error fetching scopes:', message);
            setError(message);
            setDenied(isPermissionDenied(err));
        }
        finally {
            isLoadingRef.current = false;
            setLoading(false);
        }
    }, [projectId]);
    useAccessSettlingRetry(projectId, denied, fetchScopes);
    const fetchScopesRef = useRef(fetchScopes);
    fetchScopesRef.current = fetchScopes;
    useEffect(() => {
        if (projectId) {
            fetchScopes();
        }
        else {
            setScopes([]);
            setActiveScopeId(null);
            preScopeStateRef.current = null;
        }
    }, [projectId, fetchScopes]);
    const captureScope = useCallback(async (name) => {
        const v = viewerRef.current;
        const pid = projectIdRef.current;
        if (!pid || !v) {
            throw new Error('Project and viewer are required to capture a scope');
        }
        const cameraPosition = getCameraPosition(v);
        const layerConfigs = getLayerConfigsRef.current();
        const artifactConfigs = getArtifactConfigsRef.current();
        await scopeService.createScope({
            projectId: pid,
            name,
            cameraPosition,
            layerConfigs,
            artifactConfigs,
        });
        await fetchScopesRef.current();
    }, []);
    const overrideScope = useCallback(async (scopeId) => {
        const v = viewerRef.current;
        if (!v) {
            throw new Error('Viewer is required to override a scope');
        }
        const existing = scopesRef.current.find(s => s.id === scopeId);
        if (!existing) {
            throw new Error(`Scope ${scopeId} not found`);
        }
        await scopeService.updateScope({
            scopeId,
            name: existing.name,
            cameraPosition: getCameraPosition(v),
            layerConfigs: getLayerConfigsRef.current(),
            artifactConfigs: getArtifactConfigsRef.current(),
        });
        await fetchScopesRef.current();
    }, []);
    const renameScope = useCallback(async (scopeId, newName) => {
        const trimmed = newName.trim();
        if (!trimmed) {
            throw new Error('Scope name cannot be empty');
        }
        const existing = scopesRef.current.find(s => s.id === scopeId);
        if (existing && existing.name === trimmed) {
            return;
        }
        // Fetch the full scope to preserve camera / layer / artifact state; the
        // backend's UpdateScope is a full replace, so we re-send the saved values
        // alongside the new name. Avoids needing a sparse-update backend change.
        const full = await scopeService.getScope(scopeId);
        await scopeService.updateScope({
            scopeId,
            name: trimmed,
            cameraPosition: full.cameraPosition,
            layerConfigs: full.layerConfigs,
            artifactConfigs: full.artifactConfigs.map(ac => ({
                artifactType: ac.artifactType,
                artifactId: ac.artifactId,
                visible: ac.visible,
                opacity: ac.opacity,
                colorOverride: ac.colorOverride,
                viewState: ac.viewState,
                sortOrder: ac.sortOrder,
            })),
        });
        await fetchScopesRef.current();
    }, []);
    const duplicateScope = useCallback(async (scopeId) => {
        const pid = projectIdRef.current;
        if (!pid) {
            throw new Error('Project is required to duplicate a scope');
        }
        const source = await scopeService.getScope(scopeId);
        const existingNames = new Set(scopesRef.current.map(s => s.name));
        let candidate = `${source.name} (copy)`;
        let n = 2;
        while (existingNames.has(candidate)) {
            candidate = `${source.name} (copy ${n})`;
            n += 1;
        }
        await scopeService.createScope({
            projectId: pid,
            name: candidate,
            cameraPosition: source.cameraPosition,
            layerConfigs: source.layerConfigs,
            artifactConfigs: source.artifactConfigs.map(ac => ({
                artifactType: ac.artifactType,
                artifactId: ac.artifactId,
                visible: ac.visible,
                opacity: ac.opacity,
                colorOverride: ac.colorOverride,
                viewState: ac.viewState,
                sortOrder: ac.sortOrder,
            })),
        });
        await fetchScopesRef.current();
    }, []);
    const reorderScopes = useCallback(async (scopeIds) => {
        const pid = projectIdRef.current;
        if (!pid) {
            throw new Error('Project is required to reorder scopes');
        }
        if (scopeIds.length === 0)
            return;
        const previous = scopesRef.current;
        const byId = new Map(previous.map(s => [s.id, s]));
        const next = [];
        for (const id of scopeIds) {
            const s = byId.get(id);
            if (s)
                next.push(s);
        }
        // Tail-in any scopes the caller didn't list (defensive — a stale local
        // list shouldn't drop rows from the UI).
        for (const s of previous) {
            if (!scopeIds.includes(s.id))
                next.push(s);
        }
        setScopes(next);
        try {
            await scopeService.reorderScopes(pid, scopeIds);
        }
        catch (err) {
            setScopes(previous);
            throw err;
        }
    }, []);
    const applyScope = useCallback((scopeId) => {
        var _a, _b;
        const scope = scopesRef.current.find(s => s.id === scopeId);
        const v = viewerRef.current;
        if (!scope || !v)
            return;
        if (activeScopeIdRef.current === null) {
            preScopeStateRef.current = {
                camera: getCameraPosition(v),
                layerConfigs: getLayerConfigsRef.current(),
                artifactConfigs: getArtifactConfigsRef.current(),
            };
        }
        setActiveScopeId(scopeId);
        flyToCamera(v, scope.cameraPosition);
        (_a = applyLayerConfigsRef.current) === null || _a === void 0 ? void 0 : _a.call(applyLayerConfigsRef, scope.layerConfigs);
        (_b = applyArtifactConfigsRef.current) === null || _b === void 0 ? void 0 : _b.call(applyArtifactConfigsRef, scope.artifactConfigs);
    }, []);
    const clearScope = useCallback(() => {
        var _a, _b;
        const v = viewerRef.current;
        if (!v || !preScopeStateRef.current) {
            setActiveScopeId(null);
            return;
        }
        const saved = preScopeStateRef.current;
        flyToCamera(v, saved.camera);
        (_a = applyLayerConfigsRef.current) === null || _a === void 0 ? void 0 : _a.call(applyLayerConfigsRef, saved.layerConfigs);
        (_b = applyArtifactConfigsRef.current) === null || _b === void 0 ? void 0 : _b.call(applyArtifactConfigsRef, saved.artifactConfigs);
        setActiveScopeId(null);
        preScopeStateRef.current = null;
    }, []);
    const deleteScopeFn = useCallback(async (scopeId) => {
        await scopeService.deleteScope(scopeId);
        setScopes(prev => prev.filter(s => s.id !== scopeId));
        if (activeScopeIdRef.current === scopeId) {
            setActiveScopeId(null);
            preScopeStateRef.current = null;
        }
    }, []);
    return {
        scopes,
        loading,
        error,
        activeScopeId,
        captureScope,
        applyScope,
        clearScope,
        deleteScope: deleteScopeFn,
        overrideScope,
        renameScope,
        duplicateScope,
        reorderScopes,
        scopeNames,
    };
};
